import { Directive, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { isDefined } from '@sama/js-core';
import { isEqual } from '@sama/micro-dash';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { OutputInfo } from '../../../core/h3ml/state/output-info';
import { DebouncedEventTracker } from '../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { isOutputWithAnswerOptions } from '../../../cross-team-shared/misc-utils/output-utils';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { getOutputHelper } from '../../../shared/output-helpers/get-output-helper';
import { AnswerOption } from '../../../team-apis/otterdam/answer-option';
import { CheckboxAnswer } from '../../../team-apis/otterdam/answers/checkbox-answer';
import { AbstractOutput } from '../../../team-apis/otterdam/outputs/output';
import { AbstractOutputComponent } from '../../abstract-output-component';
import { emptyAnswerOutputValidator } from './empty-answer-output-validator';

type NestableAnswer = string | CheckboxAnswer;

@Directive()
export abstract class AbstractNestableOutputComponent<
    O extends AbstractOutput = AbstractOutput,
    A extends NestableAnswer = string,
  >
  extends AbstractOutputComponent<O, A>
  implements OnInit
{
  form!: FormGroup<{ input: FormControl<A> }>;
  readOnlyText$!: Observable<A | undefined>;
  trainingAnswer$!: Observable<A | undefined>;

  protected answerOptions?: AnswerOption[];
  private eventTrackingService: EventTrackingService;
  private eventTracker: DebouncedEventTracker;

  constructor(
    injector: Injector,
    private formBuilder: FormBuilder,
  ) {
    super(injector);
    this.eventTrackingService = injector.get(EventTrackingService);
    this.eventTracker = new DebouncedEventTracker(
      { id: 'change_answer', bucket: 'tag_selection' },
      this.eventTrackingService,
    );
  }

  override ngOnInit() {
    super.ngOnInit();
    this.readOnlyText$ = this.buildReadOnlyText$();
    this.trainingAnswer$ = this.outputInfoStore('trainingAnswer').$;
    this.buildForm();
    this.setAnswerOptions();
    this.setValidators();
    this.setUpSubscriptionToOutputInfoStore();
    this.setUpSubscriptionToFormChanges();
    this.setUpSubscriptionToReadOnly();
  }

  protected setAnswerOptions() {
    const output = this.outputInfoStore.state().output;
    if (isOutputWithAnswerOptions(output)) {
      this.answerOptions = output.answer_options;
    }
  }

  protected buildReadOnlyText$() {
    return this.outputInfoStore('answer').$;
  }

  protected setValidators() {
    const output = this.outputInfoStore.state().output;
    if (output.required) {
      this.form.controls.input.setValidators(
        emptyAnswerOutputValidator(output.type),
      );
      // always call this to make sure new validators take effects
      this.form.controls.input.updateValueAndValidity();
    }
  }

  protected getUnsubmittableReason(): string {
    if (this.form.controls.input.hasError('emptyAnswer')) {
      return 'A required output is blank';
    }
    return '';
  }

  private setUpSubscriptionToReadOnly() {
    this.subscribeTo(
      this.taskWorkspaceStore('readOnly').$.pipe(distinctUntilChanged()),
      (isReadOnly) => {
        if (isReadOnly) {
          // We don't want to treat this as a valueChanges
          this.form.disable({ emitEvent: false });
        } else {
          this.form.enable({ emitEvent: false });
        }
      },
    );
  }

  private setUpSubscriptionToFormChanges() {
    this.subscribeTo(
      this.form.controls.input.valueChanges.pipe(
        startWith(this.form.controls.input.value),
        map((value) => ({
          answer: value,
          unsubmittableReason: this.getUnsubmittableReason(),
          canSaveDrafts: this.canSaveDrafts(),
          initialized: true,
        })),
      ),
      (value) => {
        this.updateStoreAndTrackEvents(value);
      },
    );
  }

  private setUpSubscriptionToOutputInfoStore() {
    this.subscribeTo(this.outputInfoStore.$, () => {
      this.syncAnswerFromStoreToForm();
    });
  }

  private syncAnswerFromStoreToForm() {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!this.outputInfoStore.state()) {
      return;
    }
    const answer = this.outputInfoStore.state().answer;
    const newValue = answer || this.emptyValue();
    this.form.setValue({ input: newValue });
    this.form.controls.input.markAllAsTouched();
  }

  private canSaveDrafts() {
    return true;
  }

  private updateStoreAndTrackEvents(newOutputInfo: Partial<OutputInfo<O, A>>) {
    let shouldTriggerTrack = false;

    this.outputInfoStore.setUsing((state) => {
      const newState = { ...state, ...newOutputInfo };
      if (isEqual(state, newState)) {
        return state;
      }
      if (
        this.shouldTrack(
          state.answer,
          newOutputInfo.answer,
          newState.initialized,
        )
      ) {
        shouldTriggerTrack = true;
      }
      newState.initialized = true;
      return newState;
    });
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (shouldTriggerTrack) {
      this.eventTracker.trackWithDebounce({});
    }
  }

  private shouldTrack(
    oldAnswer: NestableAnswer | undefined,
    newAnswer: NestableAnswer | undefined,
    isInitialized: boolean,
  ) {
    // once initialized, the empty anser can't be undefined
    // This means the old answer has to be defined; otherwise
    // we don't want to track weird intermidiate phases.
    return oldAnswer !== newAnswer && isInitialized && isDefined(oldAnswer);
  }

  private buildForm() {
    this.form = this.formBuilder.group({
      input: new FormControl<A>(this.emptyValue(), {
        nonNullable: true,
      }),
    });
  }

  private emptyValue() {
    // Timing issue: outputInfoStore is only ready in ngOnInit
    const outputHelper = getOutputHelper(
      this.outputInfoStore.state().output.type,
    );
    return outputHelper.getBlankAnswer(this.outputInfoStore.state().output);
  }
}
